import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Upload, Scissors, Wand2, Zap, Image as ImageIcon, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import GreenScreenToggle from "./GreenScreenToggle";
import useGreenScreen from "../lib/useGreenScreen";

/**
 * StudioFxModal — ONE place for every green-screen + virtual-background
 * setting + scene transitions. Consolidated in iter-181 so hosts aren't
 * juggling three separate entry points (preview chip, FX modal, admin
 * library). The modal:
 *
 *   1. Embeds `GreenScreenToggle` on the right — that component already
 *      owns Library / Recents / AI-gen / Upload / Paste-link, so we
 *      inherit every source of backgrounds for free.
 *   2. Keeps the chroma sliders + scope picker + scene transitions on
 *      the left.
 *   3. Emits a single `onApply(settings)` callback when the host presses
 *      Apply — parent components (LiveBroadcaster, DirectorConsole,
 *      LiveWaitingRoom) forward that to their `gsSettings` state which
 *      drives the `useGreenScreen` compositor hook. The outbound track
 *      swap happens automatically.
 *
 * The scope grid (`Apply FX to main / PIP / cam1-3`) is unchanged — it
 * continues to broadcast per-guest FX over the stream hub.
 */
const DEFAULTS = {
  chroma: {
    enabled: false,
    mode: "person",        // "person" = AI Selfie Segmentation, "color" = classic green key
    color: "#00FF00",
    similarity: 0.4,
    smoothness: 0.15,
    spill: true,
  },
  background: { mode: "none", dataUrl: "" },
  transition: { mode: "fade", duration_ms: 300 },
  // Scope: which outbound cameras the host wants the FX applied to.
  //   main       → the host's primary webcam
  //   secondary  → the host's second cam used for PIP
  //   cam1/2/3   → guest cam slots — when a guest is assigned to that slot
  //                and their broadcaster is active, the FX settings ride the
  //                `fx_settings` WS frame and the guest's client runs the
  //                same `useFxCompositor` pipeline on their outbound track.
  scope: { main: true, secondary: false, cam1: false, cam2: false, cam3: false },
};

export default function StudioFxModal({ open, onClose, onApply, initial, streamId }) {
  // Always shallow-merge over DEFAULTS so a partial `initial` prop (or
  // a stale localStorage object missing `background` / `transition` /
  // `scope`) can't blow up the modal on first render — iter-181 added
  // the GreenScreenToggle embed which reads `settings.background.mode`
  // unconditionally, so a missing `background` key crashes the whole
  // modal before it can mount.
  const safe = (raw) => ({
    ...DEFAULTS,
    ...(raw || {}),
    chroma: { ...DEFAULTS.chroma, ...(raw?.chroma || {}) },
    background: { ...DEFAULTS.background, ...(raw?.background || {}) },
    transition: { ...DEFAULTS.transition, ...(raw?.transition || {}) },
    scope: { ...DEFAULTS.scope, ...(raw?.scope || {}) },
  });
  const [settings, setSettings] = useState(() => safe(initial));
  const fileRef = useRef(null);
  // Live-preview state — when on, the modal grabs its own cheap
  // MediaStream and pipes it through `useGreenScreen` so the creator
  // can SEE exactly what viewers will see BEFORE hitting Apply. Uses
  // a separate stream (not the broadcaster's) so toggling preview
  // on/off in the modal never disturbs the active broadcast.
  const [preview, setPreview] = useState(false);
  const [previewStream, setPreviewStream] = useState(null);
  const previewVideoRef = useRef(null);

  // Hydrate from localStorage on first mount. `tapin_studio_fx` may
  // only hold a subset of fields (older sessions predate `scope`), so
  // we still run through `safe()` to patch any gaps.
  useEffect(() => {
    try {
      const raw = localStorage.getItem("tapin_studio_fx");
      if (raw) setSettings(safe(JSON.parse(raw)));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-hydrate settings every time the modal opens so the host sees
  // the CURRENT gsSettings (not a stale snapshot from before a setSetting
  // flipped in the parent).
  useEffect(() => {
    if (open && initial) setSettings(safe(initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patch = (path, val) =>
    setSettings((s) => {
      const [a, b] = path.split(".");
      return { ...s, [a]: { ...s[a], [b]: val } };
    });

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Upload an image (PNG/JPG/WebP)."); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Background image too big (max 8MB)."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      patch("background.dataUrl", reader.result);
      patch("background.mode", "image");
    };
    reader.readAsDataURL(f);
  };

  // Close/open side-effects: acquire a tiny camera stream when preview
  // flips on, stop it when it flips off or the modal closes. 480×270 is
  // plenty for an in-modal preview and keeps the device under no load.
  useEffect(() => {
    let cancelled = false;
    let stream = null;
    (async () => {
      if (!preview || !open) return;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 640, height: 360 },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        setPreviewStream(stream);
      } catch (e) {
        toast.error("Can't access camera for preview");
        setPreview(false);
      }
    })();
    return () => {
      cancelled = true;
      stream?.getTracks?.().forEach((t) => t.stop());
      setPreviewStream((s) => { s?.getTracks?.().forEach((t) => t.stop()); return null; });
    };
  }, [preview, open]);

  // Pipe the preview stream through the same green-screen pipeline
  // that drives the live broadcast, so WYSIWYG.
  const { track: previewTrack, bgLoading: previewBgLoading } = useGreenScreen({
    stream: previewStream,
    enabled: open && preview && !!settings.chroma?.enabled && !!previewStream,
    background: settings.background,
    chroma: settings.chroma,
  });

  // Bind the preview video element to whichever track is current:
  // the composited one when chroma is on, else the raw camera.
  useEffect(() => {
    const v = previewVideoRef.current;
    if (!v) return;
    const raw = previewStream?.getVideoTracks?.()[0];
    const t = previewTrack || raw;
    if (!t) { v.srcObject = null; return; }
    v.srcObject = new MediaStream([t]);
    v.muted = true;
    v.playsInline = true;
    v.play().catch(() => {});
  }, [previewTrack, previewStream]);

  const apply = () => {
    // 1. Fire the parent callback SYNCHRONOUSLY first so the
    //    useGreenScreen hook receives the new settings immediately —
    //    any exception in localStorage/api must not block the hook's
    //    state update.
    try {
      onApply?.(settings);
      if (typeof window !== "undefined" && window.__TAPIN_FX_DEBUG) {
        console.log("[StudioFxModal] apply — onApply fired with", settings);
      }
    } catch (e) {
      console.error("[StudioFxModal] onApply threw", e);
    }
    // 2. Persist to localStorage so the modal re-opens with the saved
    //    shape next session.
    try { localStorage.setItem("tapin_studio_fx", JSON.stringify(settings)); } catch {}
    // 3. Optional hub broadcast for guest-cam FX fan-out. Fire-and-
    //    forget so a network blip doesn't hide the Apply outcome.
    if (streamId) {
      api.post(`/live/${streamId}/fx/broadcast`, settings).catch(() => {});
    }
    // 4. User feedback.
    toast.success(
      settings.chroma?.enabled
        ? "Green screen ON — settings applied"
        : "FX settings saved",
      { id: "studio-fx-save" },
    );
    onClose?.();
  };

  /** Reset every chroma + background setting back to DEFAULTS — turns
   *  the cutout off, dumps any virtual background, and immediately
   *  pushes the cleared state through `onApply` so the live broadcast
   *  picks it up without waiting for the user to hit Apply. Also
   *  clears the localStorage blob so the next session starts clean. */
  const resetAll = () => {
    const cleared = JSON.parse(JSON.stringify(DEFAULTS));
    setSettings(cleared);
    try { onApply?.(cleared); } catch { /* ignore */ }
    try { localStorage.removeItem("tapin_studio_fx"); } catch { /* ignore */ }
    if (streamId) api.post(`/live/${streamId}/fx/broadcast`, cleared).catch(() => {});
    toast.success("Green screen reset", { id: "studio-fx-reset" });
  };

  if (!open) return null;

  // Portal the modal to `document.body` so `position:fixed` isn't
  // clipped by any ancestor `overflow-hidden` container (the
  // LiveBroadcaster wrapper + the AdvancedDirectorPanel grid both
  // have overflow:hidden, which was clipping the bottom of the
  // modal and hiding the Apply button on shorter viewports).
  return createPortal(
    <div data-testid="studio-fx-modal" className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0B0F16] border border-tv-accent/30 rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10">
          <div className="w-10 h-10 rounded-xl bg-tv-accent/20 flex items-center justify-center">
            <Wand2 className="w-5 h-5 text-tv-accent" />
          </div>
          <div>
            <div className="font-display font-black text-xl text-white">Studio FX</div>
            <div className="text-xs text-white/50">Green-screen removal · Virtual backgrounds · Scene transitions</div>
          </div>
          <div className="flex-1" />
          <button
            data-testid="studio-fx-close"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-white flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — the outer div is the scroll container; the inner grid
            collapses to its content and happily exceeds 90vh. Splitting
            scroll + grid ensures the footer (with the Apply button) is
            always visible because the overflow lives INSIDE the body,
            not in the modal's top-level flex-col. */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-6">
          <div className="grid md:grid-cols-2 gap-6">
          {/* LEFT — Controls */}
          <div className="space-y-5">
            {/* Chroma key */}
            <section className="bg-white/5 rounded-2xl border border-white/10 p-4">
              <label className="flex items-center justify-between gap-3">
                <span className="font-display font-bold text-white flex items-center gap-2">
                  <Scissors className="w-4 h-4 text-tv-accent" /> Remove Green Screen
                </span>
                <input
                  type="checkbox"
                  data-testid="fx-chroma-enabled"
                  checked={settings.chroma.enabled}
                  onChange={(e) => patch("chroma.enabled", e.target.checked)}
                  className="w-10 h-6 accent-tv-accent"
                />
              </label>
              {settings.chroma.enabled && (
                <div className="mt-3 space-y-3">
                  {/* Mode picker — AI cutout vs classic colour key */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid="fx-mode-person"
                      aria-pressed={(settings.chroma.mode || "person") === "person"}
                      onClick={() => patch("chroma.mode", "person")}
                      className={`px-3 py-2 rounded-lg text-xs font-bold border transition ${
                        (settings.chroma.mode || "person") === "person"
                          ? "bg-tv-accent text-black border-tv-accent"
                          : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                      }`}
                    >
                      Person Cutout · AI
                    </button>
                    <button
                      type="button"
                      data-testid="fx-mode-color"
                      aria-pressed={settings.chroma.mode === "color"}
                      onClick={() => patch("chroma.mode", "color")}
                      className={`px-3 py-2 rounded-lg text-xs font-bold border transition ${
                        settings.chroma.mode === "color"
                          ? "bg-tv-accent text-black border-tv-accent"
                          : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                      }`}
                    >
                      Green · Colour key
                    </button>
                  </div>
                  {settings.chroma.mode === "color" && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-white/60 w-24">Key color</span>
                      <input
                        type="color"
                        data-testid="fx-chroma-color"
                        value={settings.chroma.color}
                        onChange={(e) => patch("chroma.color", e.target.value)}
                        className="h-8 w-12 rounded cursor-pointer bg-transparent"
                      />
                      <code className="text-xs text-white/70">{settings.chroma.color}</code>
                    </div>
                  )}
                  {settings.chroma.mode === "color" && (
                    <div>
                      <div className="flex justify-between text-xs text-white/60 mb-1"><span>Similarity</span><span className="tabular-nums">{settings.chroma.similarity.toFixed(2)}</span></div>
                      <input type="range" min="0.1" max="0.8" step="0.02" value={settings.chroma.similarity} onChange={(e) => patch("chroma.similarity", parseFloat(e.target.value))} className="w-full accent-tv-accent" data-testid="fx-chroma-similarity" />
                    </div>
                  )}
                  <div>
                    <div className="flex justify-between text-xs text-white/60 mb-1">
                      <span>{(settings.chroma.mode || "person") === "person" ? "Edge feather" : "Smoothness"}</span>
                      <span className="tabular-nums">{settings.chroma.smoothness.toFixed(2)}</span>
                    </div>
                    <input type="range" min="0" max="0.4" step="0.01" value={settings.chroma.smoothness} onChange={(e) => patch("chroma.smoothness", parseFloat(e.target.value))} className="w-full accent-tv-accent" data-testid="fx-chroma-smoothness" />
                  </div>
                </div>
              )}
            </section>

            {/* Apply FX to which camera(s) — user wanted this available for Main camera too */}
            <section className="bg-white/5 rounded-2xl border border-white/10 p-4">
              <div className="font-display font-bold text-white flex items-center gap-2 mb-1">
                <Wand2 className="w-4 h-4 text-tv-accent" /> Apply FX to
              </div>
              <div className="text-[11px] text-white/50 mb-3">Green-screen + virtual background run on the outbound broadcast for the cameras you pick.</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: "main", label: "Main Camera" },
                  { key: "secondary", label: "Second Cam (PIP)" },
                  { key: "cam1", label: "Guest · CAM 1" },
                  { key: "cam2", label: "Guest · CAM 2" },
                  { key: "cam3", label: "Guest · CAM 3" },
                ].map((opt) => {
                  const on = !!settings.scope?.[opt.key];
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      data-testid={`fx-scope-${opt.key}`}
                      onClick={() => patch(`scope.${opt.key}`, !on)}
                      aria-pressed={on}
                      className={`px-3 py-2 rounded-lg text-xs font-bold border transition ${
                        on ? "bg-tv-accent text-black border-tv-accent" : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                      }`}
                    >
                      {opt.label} {on ? "· ON" : "· OFF"}
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-white/40 italic mt-2">
                Guest cam FX only fires when that guest is actually broadcasting. Main + PIP activate locally for the host.
              </div>
            </section>

            {/* Virtual background */}
            <section className="bg-white/5 rounded-2xl border border-white/10 p-4">
              <div className="font-display font-bold text-white flex items-center gap-2 mb-3">
                <ImageIcon className="w-4 h-4 text-tv-accent" /> Virtual Background
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "none", label: "None" },
                  { key: "blur", label: "Blur" },
                  { key: "image", label: "Image" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    data-testid={`fx-bg-${opt.key}`}
                    onClick={() => patch("background.mode", opt.key)}
                    aria-pressed={settings.background.mode === opt.key}
                    className={`px-3 py-2 rounded-lg text-xs font-bold border transition ${
                      settings.background.mode === opt.key
                        ? "bg-tv-accent text-black border-tv-accent"
                        : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {settings.background.mode === "image" && (
                <BackgroundGallery
                  selectedUrl={settings.background.dataUrl}
                  onChoose={(url) => patch("background.dataUrl", url)}
                />
              )}
              <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={onUpload} />
              <button
                type="button"
                data-testid="fx-bg-upload"
                onClick={() => fileRef.current?.click()}
                className="mt-3 w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm font-bold text-white"
              >
                <Upload className="w-4 h-4 text-tv-accent" /> Upload your own
              </button>
            </section>

            {/* Transitions */}
            <section className="bg-white/5 rounded-2xl border border-white/10 p-4">
              <div className="font-display font-bold text-white flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-tv-accent" /> Scene Transition
              </div>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { key: "cut", label: "Cut" },
                  { key: "fade", label: "Fade" },
                  { key: "dip_black", label: "Dip" },
                  { key: "slide_left", label: "Slide" },
                  { key: "slide_up", label: "Push" },
                ].map((t) => (
                  <button
                    key={t.key}
                    data-testid={`fx-transition-${t.key}`}
                    onClick={() => patch("transition.mode", t.key)}
                    aria-pressed={settings.transition.mode === t.key}
                    className={`px-2 py-2 rounded-lg text-[11px] font-bold border transition ${
                      settings.transition.mode === t.key
                        ? "bg-tv-accent text-black border-tv-accent"
                        : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-xs text-white/60 mb-1"><span>Duration</span><span className="tabular-nums">{settings.transition.duration_ms}ms</span></div>
                <input
                  type="range"
                  min="100" max="800" step="50"
                  value={settings.transition.duration_ms}
                  onChange={(e) => patch("transition.duration_ms", parseInt(e.target.value))}
                  className="w-full accent-tv-accent"
                  data-testid="fx-transition-duration"
                />
              </div>
            </section>
          </div>

          {/* RIGHT — Live preview + Backgrounds library */}
          <div className="space-y-3">
            {/* Live preview panel (creator-facing). Uses a SEPARATE
                camera stream from the broadcast so toggling it on/off
                never affects the outbound feed. This was explicitly
                requested to restore parity with the old GS chip. */}
            <section className="bg-white/5 rounded-2xl border border-white/10 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-display font-bold text-white flex items-center gap-2">
                  {preview ? <Eye className="w-4 h-4 text-tv-accent" /> : <EyeOff className="w-4 h-4 text-white/40" />}
                  Live Preview
                </div>
                <button
                  type="button"
                  data-testid="fx-preview-toggle"
                  onClick={() => setPreview((v) => !v)}
                  className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full transition ${
                    preview ? "bg-tv-live text-white" : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                >
                  {preview ? "Stop" : "Start"}
                </button>
              </div>
              <div className="relative aspect-video rounded-lg overflow-hidden bg-black border border-white/10">
                <video
                  ref={previewVideoRef}
                  data-testid="fx-preview-video"
                  className="w-full h-full object-cover"
                  playsInline
                  muted
                  autoPlay
                />
                {!preview && (
                  <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs text-center px-4">
                    Hit Start to preview the green-screen effect on your camera.
                  </div>
                )}
                {previewBgLoading && (
                  <div className="absolute top-2 right-2 bg-black/70 border border-white/15 text-white text-[9px] font-bold px-2 py-0.5 rounded-full animate-pulse">
                    Loading BG…
                  </div>
                )}
              </div>
              <div className="text-[10px] text-white/40 italic mt-2">
                Changes here are visible instantly — click Apply below to push them to the live stream.
              </div>
            </section>

            {/* Backgrounds: library / recents / AI / upload / link */}
            <GreenScreenToggle
              testidPrefix="studio-fx-gs"
              value={settings}
              onChange={setSettings}
              compact={false}
            />
          </div>
          </div>{/* /grid */}
        </div>{/* /body scroll */}

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10 bg-black/40">
          <div className="text-xs text-white/40">
            {settings.chroma?.enabled ? (
              <span className="text-tv-accent font-bold">● GS ready — hit Apply to go live</span>
            ) : (
              <>Saved locally — applies instantly to this session.</>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={resetAll}
              data-testid="fx-reset"
              className="px-3 py-2 rounded-full bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 font-bold text-xs uppercase tracking-wider"
              title="Turn green screen off and clear every setting"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 text-white font-bold text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="fx-apply"
              onClick={apply}
              className="px-6 py-2 rounded-full bg-tv-accent hover:bg-yellow-400 text-black font-black text-sm shadow-[0_0_16px_rgba(255,212,0,0.4)]"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * BackgroundGallery — one-tap curated backdrops + the host's previously
 * uploaded / AI-generated backgrounds + "Generate with AI" form that
 * hits Gemini Nano Banana through /api/gs/library/ai.
 */
function BackgroundGallery({ selectedUrl, onChoose }) {
  const [items, setItems] = useState({ curated: [], custom: [] });
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const refresh = React.useCallback(async () => {
    try {
      const { data } = await api.get("/gs/library");
      setItems({ curated: data?.curated || [], custom: data?.custom || [] });
    } catch { setItems((p) => ({ ...p, custom: [] })); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const generateAi = async () => {
    const p = (aiPrompt || "").trim();
    if (!p) { toast.error("Type a vibe — e.g. 'sunset rooftop, neon, miami'"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/gs/library/ai", { prompt: p });
      if (data?.data_url) {
        onChoose?.(data.data_url);
        toast.success("AI background ready — applied");
        setAiPrompt("");
        await refresh();
      } else { toast.error("Couldn't generate"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };
  const removeCustom = async (id) => {
    try { await api.delete(`/gs/library/${id}`); await refresh(); }
    catch { toast.error("Couldn't remove"); }
  };
  const Tile = ({ url, title, onClick, onRemove, testid }) => (
    <button
      type="button" data-testid={testid} onClick={onClick} title={title}
      aria-pressed={selectedUrl === url}
      className={`relative aspect-video rounded-lg overflow-hidden border-2 transition group ${
        selectedUrl === url ? "border-tv-accent shadow-[0_0_0_2px_rgba(255,212,0,0.35)]"
                            : "border-white/10 hover:border-white/30"
      }`}
    >
      <img src={url} alt={title} crossOrigin="anonymous" className="w-full h-full object-cover" loading="lazy" />
      <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[9px] uppercase tracking-wider font-black px-1.5 py-1 text-left truncate">{title}</span>
      {onRemove && (
        <span role="button" tabIndex={0} aria-label="Remove background"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onRemove(); } }}
          className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer hover:bg-red-500"
        >×</span>
      )}
    </button>
  );
  return (
    <div data-testid="fx-bg-gallery" className="mt-3 space-y-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-black text-white/55 mb-1.5">Curated</div>
        <div className="grid grid-cols-3 gap-1.5">
          {(items.curated || []).map((c) => (
            <Tile key={c.id} testid={`fx-bg-tile-${c.id}`} url={c.url} title={c.title} onClick={() => onChoose?.(c.url)} />
          ))}
        </div>
      </div>
      <div className="border-t border-white/10 pt-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-black text-tv-accent mb-1.5 flex items-center gap-1">
          <Wand2 className="w-3 h-3" /> Generate with AI
        </div>
        <div className="flex gap-1.5">
          <input type="text" data-testid="fx-bg-ai-prompt" value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder="Sunset rooftop, neon Miami at dusk…"
            disabled={generating} maxLength={200}
            className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-[11px] text-white outline-none focus:border-tv-accent disabled:opacity-50"
          />
          <button type="button" data-testid="fx-bg-ai-generate" onClick={generateAi}
            disabled={generating || !aiPrompt.trim()}
            className="bg-tv-accent hover:bg-yellow-400 text-black text-[11px] font-black uppercase tracking-wider px-3 py-1.5 rounded disabled:opacity-50"
          >{generating ? "…" : "Generate"}</button>
        </div>
        <div className="text-[9px] text-white/40 italic mt-1">Generated by Gemini Nano Banana — saves to your library.</div>
      </div>
      {items.custom?.length > 0 && (
        <div className="border-t border-white/10 pt-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-black text-white/55 mb-1.5">My backgrounds</div>
          <div className="grid grid-cols-3 gap-1.5">
            {items.custom.map((c) => (
              <Tile key={c.id} testid={`fx-bg-tile-${c.id}`} url={c.data_url}
                title={c.title || (c.source === "ai" ? "AI" : "Upload")}
                onClick={() => onChoose?.(c.data_url)}
                onRemove={() => removeCustom(c.id)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
